// New CardRow used by CardsPanel inside Section/SubSection containers.
//
// Visual contract — inspired by prototype cases (uc-01-founder-pitch +
// _shared.jsx DCard) and product design A-warm-paper.html:
//   - 3px solid left rule in spec.rule color
//   - Per-kind background fill (decision/question/observation get tinted)
//   - Title in larger weight; body inline below (3-line clamp, expandable)
//   - Edge chips beneath body (parsed from wikilinks in body)
//   - BY AGENT / BY YOU author badge, top-right, mono micro
//   - Pin (📌) marker when placement is overridden by user
//   - Drag handle for frame placement override

import { createContext, memo, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Card, ImplementationStatus } from '../../api';
import { specForKind } from '../../kinds';
import { parseEdges, parseAllRefs, edgeArrow, edgeColor } from '../../edges';
import { CardBodyMarkdown } from './CardBodyMarkdown';
import { ComputedBadge, getCardComputed } from './ComputedProvenance';

export interface CardRowProps {
  card: Card;
  overridden: boolean;
  selected: boolean;
  flash: boolean;
  /** True when ANOTHER card in the panel is being hovered AND its body
   *  references this card by `[[…]]`. Renders a soft amber outline so the
   *  user can see at a glance which cards a hovered one cites. NOT
   *  scrolled — staying out of the user's way is the whole point. */
  highlighted?: boolean;
  /** Set of card ids that exist in this project. CardRow subtracts these
   *  from the body's `[[…]]` references to render broken refs in red.
   *  Pass an empty set to disable the check. */
  knownIds: ReadonlySet<string>;
  /** Used by inner affordances (e.g. broken-ref jumps) and as a fallback if
   *  no peek handler is wired. Row-level single-click goes to onPeek. */
  onSelect: (id: string) => void;
  /** Single-click. Opens a floating peek popover with the full card body.
   *  Anchor is the card row's bounding rect at the time of the click — used
   *  by the peek to position itself next to the originating card. The peek
   *  also takes care of selection so the row gets the same highlight. */
  onPeek: (id: string, anchor: DOMRect) => void;
  /** Emits the body's resolved `[[…]]` ref ids on mouseenter (filters out
   *  self and unknown targets), and `[]` on mouseleave. The parent uses
   *  this to drive the `highlighted` prop on the cited rows. */
  onHoverRefs?: (refs: string[]) => void;
  /** Open the history modal for an arbitrary card id. Used by broken-ref
   *  chips: clicking [[c-43]] when c-43 was deleted opens its history so
   *  the user can see what was deleted, by whom, and why. */
  onShowHistory?: (cardId: string) => void;
  /** Click handler for the override pin (📌). When provided AND the card
   *  is overridden, the pin becomes a button — clicking drops the override
   *  on the server so the LLM/rule pass can re-place this card on the
   *  next frame view. Omit to render an inert pin (e.g. in views where
   *  no frame is active). */
  onResetOverride?: (cardId: string) => void;
  /** When true, the kind glyph + label is dropped from the top row (the
   *  parent already groups by kind, so the label would just repeat the
   *  section header above). Card id, pin, stale, ⚡ etc. all still render.
   *  Default false. */
  compactKind?: boolean;
}

/** Set of card ids touched by an external MCP client in the trailing 24h.
 *  Provided once at the top of CardsPanel so CardRow can render a ⚡ glyph
 *  without every nested subcomponent threading the prop through. Empty
 *  default = no badges (also what we render when MCP is offline). */
export const McpRecentContext = createContext<ReadonlySet<string>>(new Set());

function CardRowImpl({
  card, overridden, selected, flash, highlighted, knownIds, onSelect, onPeek, onHoverRefs, onShowHistory, onResetOverride, compactKind = false,
}: CardRowProps) {
  const { t } = useTranslation();
  const mcpRecent = useContext(McpRecentContext);
  const mcpRecentlyRead = mcpRecent.has(card.card_id);
  const spec = specForKind(card.kind);
  const [expanded, setExpanded] = useState(false);
  const edges = parseEdges(card.body);
  const brokenRefs = parseAllRefs(card.body).filter(
    (id) => id !== card.card_id && !knownIds.has(id),
  );
  const computed = getCardComputed(card);

  const ref = useRef<HTMLElement>(null);
  // Scroll into view when this card becomes the selection target or flashes
  // from an SSE write — covers Gap-Ledger / write_card events that fire on
  // a card that's currently below the fold. `block: 'nearest'` is a no-op
  // when the card is already visible, so this is harmless on direct clicks.
  useEffect(() => {
    if (selected || flash) {
      ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selected, flash]);

  // Each kind gets a distinct visual register. We tweak title weight, italic,
  // and bg tint per spec instead of having one uniform card.
  const kindTreatment = treatmentFor(card.kind);

  return (
    <article
      ref={ref}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', card.card_id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={(e) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        onPeek(card.card_id, rect);
      }}
      onMouseEnter={() => {
        if (!onHoverRefs) return;
        const refs = parseAllRefs(card.body).filter(
          (id) => id !== card.card_id && knownIds.has(id),
        );
        if (refs.length > 0) onHoverRefs(refs);
      }}
      onMouseLeave={() => {
        if (onHoverRefs) onHoverRefs([]);
      }}
      style={{
        margin: '0 0 10px',
        padding: '11px 14px 12px 14px',
        background: kindTreatment.bg,
        border: '1px solid',
        borderColor: selected
          ? 'var(--blue)'
          : flash
          ? 'var(--blue)'
          : highlighted
          ? 'var(--amber)'
          : kindTreatment.borderSoft ?? 'transparent',
        borderLeft: `3px solid ${spec.rule}`,
        borderRadius: 4,
        boxShadow: selected
          ? '0 0 0 2px var(--blue), 0 1px 0 rgba(0,0,0,0.02)'
          : flash
          ? '0 0 0 2px rgba(37,99,235,0.18)'
          : highlighted
          ? '0 0 0 2px var(--amber)'
          : 'none',
        opacity: card.stale ? 0.55 : 1,
        cursor: 'pointer',
        transition: 'border-color 120ms, box-shadow 160ms',
      }}
    >
      {/* Top row: kind label + id + (optional pin) + stale + author */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 6,
          marginBottom: 6,
        }}
      >
        {!compactKind && (
          <span
            className="mono"
            style={{
              fontSize: 9.5,
              color: spec.color,
              letterSpacing: '.12em',
              fontWeight: 700,
            }}
          >
            {spec.glyph} {spec.label}
          </span>
        )}
        <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)' }}>
          {card.card_id}
        </span>
        {overridden && (
          onResetOverride ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onResetOverride(card.card_id);
              }}
              title={t('cardRow.pinResetTooltip')}
              style={{
                fontSize: 10,
                lineHeight: 1,
                padding: '1px 3px',
                margin: 0,
                background: 'transparent',
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--paper-1)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              📌
            </button>
          ) : (
            <span title={t('cardRow.pinManualTooltip')} style={{ fontSize: 10, lineHeight: 1 }}>
              📌
            </span>
          )
        )}
        {card.stale && (
          <span
            className="mono"
            style={{
              fontSize: 9.5,
              color: 'var(--red)',
              fontWeight: 700,
              letterSpacing: '.05em',
            }}
          >
            {t('cardRow.stale')}
          </span>
        )}
        <ImplementationStatusPill status={card.implementation_status} />
        {computed && <ComputedBadge />}

        {mcpRecentlyRead && (
          <span
            title={t('cardRow.mcpRecentTooltip')}
            style={{
              fontSize: 10,
              color: '#3a8e5a',
              lineHeight: 1,
            }}
          >
            ⚡
          </span>
        )}
        <span style={{ flex: 1 }} />
        {/* doc/11 PR-A3: 4-state author chip.
            - user (blue):           BY YOU         — user created from scratch
            - agent (ink-3):         BY AGENT       — write_card direct
            - ai-suggested (amber):  SUGGESTED      — propose_card, awaiting review
            - user-revised (violet): REVISED        — user edited an agent/ai card
            Color encodes "how was this born": cold/quiet for AI, warm for human
            touch, amber for "still in flux", violet for "human edited AI". */}
        <span
          className="mono"
          style={{
            fontSize: 9,
            color: authorChipColor(card.author),
            letterSpacing: '.1em',
            fontWeight: 600,
          }}
          title={
            card.author === 'user-revised' && typeof card.extra?.prior_author === 'string'
              ? `Originally ${String(card.extra.prior_author)}, edited by you`
              : undefined
          }
        >
          {authorChipLabel(card.author, t)}
        </span>
      </div>

      {/* Title — required for cards but defensively handle empty. dead_end
          cards get line-through always (it's the visual marker of "this
          path is dead", per doc/11 PR-A1). Stale cards also get line-through
          per the existing convention; the two are visually identical because
          they're semantically related ("don't use this") even though stored
          differently. */}
      {card.title && (
        <div
          style={{
            fontSize: 13.5,
            fontWeight: kindTreatment.titleWeight,
            fontStyle: kindTreatment.titleItalic ? 'italic' : 'normal',
            color: card.kind === 'dead_end' ? 'var(--ink-3)' : 'var(--ink-0)',
            lineHeight: 1.4,
            marginBottom: card.body ? 5 : 0,
            textDecoration: (card.stale || card.kind === 'dead_end') ? 'line-through' : 'none',
          }}
        >
          {card.title}
        </div>
      )}

      {/* doc/11 PR-A2: decision cards with evidence/alternatives chains get
          a one-line inline subtitle "(based on N evidence · N alternatives)"
          so list-view readers see at a glance that the decision has structured
          backing. The full chips live in CardPeek's DecisionExtras section. */}
      {card.kind === 'decision' && (() => {
        const ev = Array.isArray(card.extra?.evidence) ? (card.extra.evidence as unknown[]).length : 0;
        const alt = Array.isArray(card.extra?.alternatives) ? (card.extra.alternatives as unknown[]).length : 0;
        if (ev === 0 && alt === 0) return null;
        const parts: string[] = [];
        if (ev > 0) parts.push(`based on ${ev} evidence`);
        if (alt > 0) parts.push(`${alt} alternative${alt > 1 ? 's' : ''}`);
        return (
          <div
            className="mono"
            style={{
              fontSize: 10.5,
              color: 'var(--ink-3)',
              letterSpacing: '.02em',
              marginBottom: 5,
              fontStyle: 'italic',
            }}
          >
            ({parts.join(' · ')})
          </div>
        );
      })()}

      {/* doc/11 PR-A1: surface the *why* fields from extra above the body
          so cross-thread readers see them without opening CardPeek:
          - dead_end → extra.failure_mode (required field, "why it failed")
          - pivot → extra.trigger + extra.from_card_id (required, "from X why Y")
          Renders as a small italicized strip in --ink-3 so it reads as
          metadata, not content. */}
      {card.kind === 'dead_end' && typeof card.extra?.failure_mode === 'string' && (
        <div
          style={{
            fontSize: 11.5,
            lineHeight: 1.5,
            color: 'var(--ink-3)',
            fontStyle: 'italic',
            marginBottom: 5,
            paddingLeft: 8,
            borderLeft: '2px solid var(--rule)',
          }}
        >
          Failure mode: {String(card.extra.failure_mode)}
        </div>
      )}
      {card.kind === 'pivot' && (typeof card.extra?.trigger === 'string' || typeof card.extra?.from_card_id === 'string') && (
        <div
          style={{
            fontSize: 11.5,
            lineHeight: 1.5,
            color: 'var(--amber)',
            fontStyle: 'italic',
            marginBottom: 5,
            paddingLeft: 8,
            borderLeft: '2px solid var(--amber)',
          }}
        >
          {typeof card.extra?.from_card_id === 'string' && (
            <>From <span className="mono">{String(card.extra.from_card_id)}</span>{typeof card.extra?.trigger === 'string' ? ' — ' : ''}</>
          )}
          {typeof card.extra?.trigger === 'string' && String(card.extra.trigger)}
        </div>
      )}

      {/* Body — rendered as markdown; clamped via max-height when collapsed.
          Block elements (h2, ul, table) break webkit-line-clamp, so we cap by
          height instead and let the "+ more" toggle reveal the rest. */}
      {card.body && (
        <div
          className="md-card-body"
          style={{
            fontSize: 12.5,
            lineHeight: 1.6,
            color: 'var(--ink-2)',
            fontStyle: kindTreatment.bodyItalic ? 'italic' : 'normal',
            ...(expanded
              ? {}
              : {
                  maxHeight: '4.8em',
                  overflow: 'hidden',
                }),
          }}
        >
          <CardBodyMarkdown
            body={bodyWithoutEdgeLines(card.body)}
            knownIds={knownIds}
            onJumpToCard={onSelect}
          />
        </div>
      )}

      {/* Expand/collapse — only if body actually overflows 3 lines (rough heuristic) */}
      {card.body.length > 140 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          className="mono"
          style={{
            fontSize: 9.5,
            color: 'var(--ink-3)',
            letterSpacing: '.05em',
            marginTop: 4,
            padding: '1px 4px',
            cursor: 'pointer',
          }}
        >
          {expanded ? t('cardRow.less') : t('cardRow.more')}
        </button>
      )}

      {/* Edge chips */}
      {edges.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 8,
            paddingLeft: 2,
          }}
        >
          {edges.map((e, i) => {
            const known = knownIds.has(e.targetId) || e.targetId === card.card_id;
            const Tag: 'button' | 'span' = known ? 'button' : 'span';
            return (
              <Tag
                key={i}
                className="mono"
                onClick={
                  known
                    ? (ev) => {
                        ev.stopPropagation();
                        onSelect(e.targetId);
                      }
                    : undefined
                }
                title={known ? t('cardRow.jumpTo', { id: e.targetId }) : t('cardRow.unknownCard')}
                style={{
                  fontSize: 10,
                  color: edgeColor(e.type),
                  letterSpacing: '.04em',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  font: 'inherit',
                  cursor: known ? 'pointer' : 'default',
                }}
              >
                <span style={{ marginRight: 3 }}>{edgeArrow(e.type)}</span>
                {e.type} {e.targetId}
                {!known && (
                  <span style={{ color: 'var(--red)', marginLeft: 4 }}>?</span>
                )}
              </Tag>
            );
          })}
        </div>
      )}

      {/* Broken-ref warning row — only refs that aren't already shown as
          edge chips above. Edges chips already mark broken with a red `?`. */}
      {brokenRefs.length > 0 && brokenRefs.some((id) => !edges.some((e) => e.targetId === id)) && (
        <div
          className="mono"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginTop: 6,
            paddingLeft: 2,
            fontSize: 10,
            color: 'var(--red)',
            letterSpacing: '.04em',
          }}
          title={t('cardRow.brokenRefsTooltip')}
        >
          <span style={{ fontWeight: 700 }}>{t('cardRow.brokenRefs')}</span>
          {brokenRefs
            .filter((id) => !edges.some((e) => e.targetId === id))
            .map((id) =>
              onShowHistory ? (
                <button
                  key={id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowHistory(id);
                  }}
                  title={t('cardRow.seeHistoryOfDeleted', { id })}
                  className="mono"
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    font: 'inherit',
                    color: 'var(--red)',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                    textDecorationStyle: 'dotted',
                  }}
                >
                  [[{id}]]
                </button>
              ) : (
                <span key={id}>[[{id}]]</span>
              ),
            )}
        </div>
      )}
    </article>
  );
}

/**
 * Memo around CardRow with a custom comparator that intentionally IGNORES
 * function-prop identity. Why: callbacks like `onSelect` / `onPeek` /
 * `onHoverRefs` come from App.tsx and are recreated on every parent
 * render — wrapping each in `useCallback` upstream would touch ~6 call
 * sites and still be brittle. Comparing only data props lets a parent
 * re-render that doesn't change CARD STATE (e.g. opening a modal,
 * unrelated state change) skip the entire CardRow subtree, which on a
 * 95-card project saves ~200ms of reconciliation per pass.
 *
 * The comparator is conservative: any visible-state change (selection,
 * flash, hover-highlight, override pin, card content) DOES re-render.
 * `knownIds` is reference-compared because it comes from a `useMemo`
 * keyed on `cards` in CardsPanel, so equal references mean the broken-
 * refs set hasn't changed.
 */
function cardRowPropsEqual(prev: CardRowProps, next: CardRowProps): boolean {
  return (
    prev.card === next.card &&
    prev.overridden === next.overridden &&
    prev.selected === next.selected &&
    prev.flash === next.flash &&
    (prev.highlighted ?? false) === (next.highlighted ?? false) &&
    prev.knownIds === next.knownIds &&
    (prev.compactKind ?? false) === (next.compactKind ?? false)
    // onSelect / onPeek / onHoverRefs / onShowHistory / onResetOverride
    // intentionally NOT compared — see docstring.
  );
}

export const CardRow = memo(CardRowImpl, cardRowPropsEqual);

interface KindTreatment {
  bg: string;
  borderSoft?: string;
  titleWeight: number;
  titleItalic: boolean;
  bodyItalic: boolean;
}

function treatmentFor(kind: string): KindTreatment {
  switch (kind) {
    case 'decision':
      return {
        bg: 'var(--paper-1)',
        borderSoft: 'var(--rule)',
        titleWeight: 700,
        titleItalic: false,
        bodyItalic: false,
      };
    case 'question':
      return {
        bg: 'var(--amber-soft)',
        borderSoft: 'transparent',
        titleWeight: 600,
        titleItalic: true,
        bodyItalic: true,
      };
    case 'gap':
      return {
        bg: 'var(--amber-soft)',
        borderSoft: 'var(--red)',
        titleWeight: 700,
        titleItalic: false,
        bodyItalic: false,
      };
    case 'observation':
      return {
        bg: 'var(--paper-1)',
        borderSoft: 'var(--rule)',
        titleWeight: 500,
        titleItalic: false,
        bodyItalic: false,
      };
    case 'note':
      return {
        bg: 'var(--paper-0)',
        borderSoft: 'var(--rule)',
        titleWeight: 400,
        titleItalic: false,
        bodyItalic: false,
      };
    // doc/11 PR-A1: dead_end = "this path is dead". Visually muted (faded paper
    // bg, soft border) so dead ends don't dominate list view but remain searchable
    // for cross-thread "don't repeat this" recall. Title gets line-through via
    // an inline style in CardRowImpl when kind === 'dead_end'.
    case 'dead_end':
      return {
        bg: 'var(--paper-0)',
        borderSoft: 'var(--rule)',
        titleWeight: 500,
        titleItalic: false,
        bodyItalic: true,
      };
    // pivot = direction change. Amber-tinted to signal "state change happened
    // here" — the from→to chain renders in the body via a dedicated section.
    case 'pivot':
      return {
        bg: 'var(--paper-1)',
        borderSoft: 'var(--amber)',
        titleWeight: 600,
        titleItalic: true,
        bodyItalic: false,
      };
    case 'statement':
    default:
      return {
        bg: 'white',
        borderSoft: 'var(--rule)',
        titleWeight: 600,
        titleItalic: false,
        bodyItalic: false,
      };
  }
}

/** PR-41: small status pill rendered next to the kind label. Returns null
 *  for the common case (`status == null` — most cards aren't shipped
 *  artifacts) so we don't add visual noise. Color uses the existing
 *  palette variables so it lines up with the kind colors. Exported
 *  separately so CardPeek can render the same pill in its header. */
export function ImplementationStatusPill({
  status,
}: {
  status: ImplementationStatus | null;
}) {
  const { t } = useTranslation();
  if (!status) return null;
  const meta = IMPL_STATUS_META[status];
  return (
    <span
      className="mono"
      title={t(`cardRow.implStatus.${status}.tooltip`)}
      style={{
        fontSize: 9.5,
        color: meta.color,
        fontWeight: 700,
        letterSpacing: '.06em',
        padding: '0 4px',
        borderRadius: 3,
        background: meta.bg,
        border: `1px solid ${meta.border}`,
        lineHeight: 1.5,
      }}
    >
      {t(`cardRow.implStatus.${status}.label`)}
    </span>
  );
}

interface ImplStatusMeta {
  color: string;
  bg: string;
  border: string;
}

/** Static palette for the four implementation states. Reuses the project's
 *  existing CSS vars so a future palette tweak ripples through; only the
 *  drift state escalates to red because it's the one that demands action.
 *  Label + tooltip are i18n keys resolved at render time in the pill. */
const IMPL_STATUS_META: Record<ImplementationStatus, ImplStatusMeta> = {
  proposed: {
    color: 'var(--ink-2)',
    bg: 'var(--paper-1)',
    border: 'var(--rule)',
  },
  building: {
    color: '#8a5a00',
    bg: 'var(--amber-soft)',
    border: 'var(--amber)',
  },
  built: {
    color: '#1e6e3a',
    bg: '#eaf6ec',
    border: '#9ed4ad',
  },
  drift_detected: {
    color: 'var(--red)',
    bg: '#fdecec',
    border: 'var(--red)',
  },
};

/**
 * doc/11 PR-A3 — 4-state author chip color + label helpers. Keyed by the
 * `cards.author` text column. Unknown values fall back to "BY AGENT"-style
 * gray so a new server value never breaks the UI.
 */
function authorChipColor(author: string): string {
  switch (author) {
    case 'user':
      return 'var(--blue)';
    case 'ai-suggested':
      return 'var(--amber)';
    case 'user-revised':
      return 'var(--violet)';
    case 'agent':
    default:
      return 'var(--ink-3)';
  }
}

function authorChipLabel(author: string, t: (k: string) => string): string {
  switch (author) {
    case 'user':
      return t('cardRow.byYou');
    case 'ai-suggested':
      return t('cardRow.bySuggested');
    case 'user-revised':
      return t('cardRow.byRevised');
    case 'agent':
    default:
      return t('cardRow.byAgent');
  }
}

/**
 * Strip lone edge-declaration lines (`→supports [[c-7]]`) out of the rendered
 * body — they're surfaced as edge chips below. Lines that mention edges
 * inline alongside prose stay intact.
 */
function bodyWithoutEdgeLines(body: string): string {
  return body
    .split('\n')
    .filter((line) => {
      const trimmed = line.trim();
      // Drop a line iff it consists entirely of one or more typed wikilinks.
      if (trimmed === '') return true;
      const stripped = trimmed.replace(
        /(?:→|->)\s*[a-z][a-z0-9-]*\s*\[\[[A-Za-z0-9_-]+\]\]\s*/g,
        '',
      );
      return stripped.length > 0;
    })
    .join('\n')
    .trim();
}
